import apiClient from "./api";

export interface PromptArgument {
  name: string;
  description?: string;
  required: boolean;
}

export interface PromptConfig {
  id: string;
  serverId: string;
  name: string;
  description: string;
  promptTemplate: string;
  argumentsJson?: string;
  isActive: boolean;
}

export interface UpdatePromptRequest {
  description?: string;
  promptTemplate?: string;
  argumentsJson?: string;
}

export interface CreatePromptRequest {
  name: string;
  description?: string;
  promptTemplate: string;
  argumentsJson?: string;
  isActive?: boolean;
}

/**
 * Tenant-wide prompt shortcut surfaced to widget end-users (chip strip + slash
 * dropdown). Differs from MCP `PromptConfig` (server-scoped, MCP-arg templated)
 * and from `WidgetShortcutCard` (welcome-screen only, 8-card cap).
 */
/**
 * Per-language override of a prompt shortcut. A blank field falls back to the base
 * value, so a translation may cover only some of the three fields.
 *
 * Declared as a type alias rather than an interface so it carries an implicit index
 * signature and can be handed straight to the generic `TranslationTabs` editor.
 */
export type PromptShortcutTranslation = {
  name?: string;
  description?: string;
  body?: string;
};

export interface PromptShortcut {
  id: string;
  scope: "Server" | "Company" | "CustomerCompany" | number;
  companyId?: string | null;
  customerCompanyId?: string | null;
  name: string;
  description?: string | null;
  body: string;
  sortOrder: number;
  icon?: string | null;
  isActive: boolean;
  /**
   * Language code → translated fields. Always null for CustomerCompany-scoped rows —
   * only tenant shortcuts are translated.
   */
  translations?: Record<string, PromptShortcutTranslation> | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePromptShortcutRequest {
  name: string;
  description?: string;
  body: string;
  icon?: string;
  sortOrder?: number;
  isActive?: boolean;
  translations?: Record<string, PromptShortcutTranslation>;
}

export interface UpdatePromptShortcutRequest {
  name?: string;
  description?: string;
  body?: string;
  icon?: string;
  sortOrder?: number;
  isActive?: boolean;
  /** Omit to leave stored translations unchanged; send `{}` to clear them. */
  translations?: Record<string, PromptShortcutTranslation>;
}

export const promptService = {
  getServerPrompts: async (serverId: string): Promise<PromptConfig[]> => {
    const response = await apiClient.get(`/servers/${serverId}/prompts`);
    return response.data;
  },

  getServerPromptsPaged: async (
    serverId: string,
    page: number = 1,
    pageSize: number = 50,
    searchQuery?: string,
    isActive?: boolean,
  ): Promise<{
    items: PromptConfig[];
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  }> => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("pageSize", pageSize.toString());
    if (searchQuery) params.append("searchQuery", searchQuery);
    if (isActive !== undefined) params.append("isActive", isActive.toString());

    const response = await apiClient.get(
      `/servers/${serverId}/prompts/paged?${params.toString()}`,
    );
    return response.data;
  },

  createPrompt: async (
    serverId: string,
    data: CreatePromptRequest,
  ): Promise<PromptConfig> => {
    const response = await apiClient.post(`/servers/${serverId}/prompts`, data);
    return response.data;
  },

  updatePrompt: async (
    serverId: string,
    promptId: string,
    data: UpdatePromptRequest,
  ): Promise<PromptConfig> => {
    const response = await apiClient.put(
      `/servers/${serverId}/prompts/${promptId}`,
      data,
    );
    return response.data;
  },

  togglePromptStatus: async (
    serverId: string,
    promptId: string,
    isActive: boolean,
  ): Promise<PromptConfig> => {
    const response = await apiClient.patch(
      `/servers/${serverId}/prompts/${promptId}/status`,
      { isActive },
    );
    return response.data;
  },

  deletePrompt: async (serverId: string, promptId: string): Promise<void> => {
    await apiClient.delete(`/servers/${serverId}/prompts/${promptId}`);
  },

  // ── Tenant-wide widget shortcuts (chip strip + slash command) ──────────
  listCompanyShortcuts: async (): Promise<PromptShortcut[]> => {
    const response = await apiClient.get("/prompt-shortcuts");
    return response.data;
  },

  createCompanyShortcut: async (
    data: CreatePromptShortcutRequest,
  ): Promise<PromptShortcut> => {
    const response = await apiClient.post("/prompt-shortcuts", data);
    return response.data;
  },

  updateCompanyShortcut: async (
    id: string,
    data: UpdatePromptShortcutRequest,
  ): Promise<PromptShortcut> => {
    const response = await apiClient.put(`/prompt-shortcuts/${id}`, data);
    return response.data;
  },

  toggleCompanyShortcutStatus: async (
    id: string,
    isActive: boolean,
  ): Promise<PromptShortcut> => {
    const response = await apiClient.patch(`/prompt-shortcuts/${id}/status`, {
      isActive,
    });
    return response.data;
  },

  deleteCompanyShortcut: async (id: string): Promise<void> => {
    await apiClient.delete(`/prompt-shortcuts/${id}`);
  },

  reorderCompanyShortcuts: async (
    items: Array<{ id: string; sortOrder: number }>,
  ): Promise<PromptShortcut[]> => {
    const response = await apiClient.post("/prompt-shortcuts/reorder", {
      items,
    });
    return response.data;
  },

  parseArguments: (argumentsJson?: string): PromptArgument[] | null => {
    if (!argumentsJson) return null;
    try {
      return JSON.parse(argumentsJson);
    } catch {
      return null;
    }
  },

  stringifyArguments: (args: PromptArgument[]): string => {
    return JSON.stringify(args);
  },
};
